import { formValueSelector } from "redux-form";
import { formName } from "../Constants/constants";
import { contactTypePerson } from "../Constants/contactTypes";
import { getOccurrenceGl, getHostLiquorItem } from "Selectors/Limits";
import { isWeddingTypeEventSelected } from "Selectors/Event";
import { isRequiredCondition } from "@gbli-events/common/src/Helpers/UnderwritingQuestions";
import { isVenueInNorthDakota } from "../Selectors/Payment";
import { dateHelpers } from "@jauntin/utilities";
import { InitSessionPayload } from "./PaymentService";
import { getTotalAmount } from "Selectors/Quote";

const formValues = formValueSelector(formName);

// Redux store is not globally typed in this project; we type only the slices we read.
type AppState = {
  form: {
    application: {
      values: {
        eventType?: { value?: { identifier?: string } };
        [key: string]: unknown;
      };
    };
  };
  formPage: {
    venue: {
      selectedPlaceId: string;
      selectedPlaceAddressComponents: {
        companyName: string | null;
        address1: string;
        city: string;
        state: string;
        country: string;
        zip: string;
      };
      selectedPlaceUtcOffset: number;
    };
    insuranceContact: { contactSame: boolean };
    referral: {
      sourceCode: string | null;
      producerCode: string | null;
      subProducerCode: string | null;
      facilityCode: string | null;
      boilerplate: unknown[] | null;
    };
    summary: {
      additionalCertHolders: InitSessionPayload["additionalInsureds"];
    };
  };
};

function getAdditionalCoverages(
  state: AppState,
): InitSessionPayload["additionalCoverage"] {
  const hostLiquorItem = getHostLiquorItem(state);
  const personalAndAdvertisingLimit: number = getOccurrenceGl(state);
  const productsAndCompletedOperations: number = getOccurrenceGl(state);
  const terrorism: string | null = formValues(state, "ACTerrorism");
  const damageToRentedProperty = formValues(state, "ACDamageToRentedProperty");
  const waiverOfTransferRights = formValues(state, "ACWaiverOfTransferRights");
  const medical = 5000;
  const cannabis: string | null = formValues(state, "cannabis");

  return [
    {
      type: "host-liquor",
      value: hostLiquorItem.coverageDescription === "Included",
    },
    {
      type: "personal-and-advertising-limit",
      value: personalAndAdvertisingLimit,
    },
    {
      type: "products-and-completed-operations",
      value: cannabis !== "1" ? productsAndCompletedOperations : null,
    },
    {
      type: "terrorism",
      value: terrorism === "1",
    },
    {
      type: "damage-to-rented-premises",
      value: damageToRentedProperty,
    },
    {
      type: "waiver-of-transfer-rights",
      value: waiverOfTransferRights,
    },
    {
      type: "medical",
      value: medical,
    },
    {
      type: "cannabis",
      value: cannabis === "1",
    },
  ];
}

function getRenterContactType(state: AppState): string {
  const indivOrCompany: string = formValues(state, "insuredRenter");
  if (indivOrCompany === contactTypePerson) {
    return indivOrCompany;
  }
  return formValues(state, "insuredCompanyType");
}

function getPaymentBody(state: AppState) {
  const contactType = getRenterContactType(state);
  const isPersonRenter = contactType === contactTypePerson;

  const renterAddress: InitSessionPayload["renterAddress"][0] = isPersonRenter
    ? {
        contactType,
        contactFirstName: formValues(state, "insuredFirstName"),
        contactLastName: formValues(state, "insuredLastName"),
        address1: formValues(state, "insuredAddress"),
        address2: formValues(state, "insuredAddress2"),
        city: formValues(state, "insuredCity"),
        state: formValues(state, "insuredState"),
        country: formValues(state, "insuredCountry"),
        zip: formValues(state, "insuredZip"),
      }
    : {
        contactType,
        companyName: formValues(state, "insuredCompany"),
        companyType: formValues(state, "insuredRenterType"),
        address1: formValues(state, "insuredAddress"),
        address2: formValues(state, "insuredAddress2"),
        city: formValues(state, "insuredCity"),
        state: formValues(state, "insuredState"),
        country: formValues(state, "insuredCountry"),
        zip: formValues(state, "insuredZip"),
      };

  const contactSame = state.formPage.insuranceContact.contactSame;

  // When contactSame is false, the contact is a different person (always type Person)
  // but shares the insured's address.
  const insuranceContactAddress: InitSessionPayload["insuranceContactAddress"][0] =
    contactSame
      ? {
          contactType,
          contactFirstName: isPersonRenter
            ? formValues(state, "insuredFirstName")
            : formValues(state, "contactFirstName"),
          contactLastName: isPersonRenter
            ? formValues(state, "insuredLastName")
            : formValues(state, "contactLastName"),
          companyName: !isPersonRenter
            ? formValues(state, "insuredCompany")
            : undefined,
          address1: formValues(state, "insuredAddress"),
          address2: formValues(state, "insuredAddress2"),
          city: formValues(state, "insuredCity"),
          state: formValues(state, "insuredState"),
          country: formValues(state, "insuredCountry"),
          zip: formValues(state, "insuredZip"),
        }
      : {
          contactType: contactTypePerson,
          contactFirstName: formValues(state, "contactFirstName"),
          contactLastName: formValues(state, "contactLastName"),
          address1: formValues(state, "insuredAddress"),
          address2: formValues(state, "insuredAddress2"),
          city: formValues(state, "insuredCity"),
          state: formValues(state, "insuredState"),
          country: formValues(state, "insuredCountry"),
          zip: formValues(state, "insuredZip"),
        };

  const formQuestions: Array<{ code: string; answer: unknown }> =
    formValues(state, "underwritingQuestions") ?? [];
  const underwritingQuestions = formQuestions.map((question) => ({
    code: question.code,
    answer: isRequiredCondition(formQuestions)(question)
      ? question.answer
      : null,
  }));

  const formOptionalVenues: Array<{
    selectedPlaceId: string;
    selectedPlaceAddressComponents: {
      companyName: string | null;
      address1: string;
      city: string;
      state: string;
      country: string;
      zip: string;
    };
    selectedPlaceUtcOffset: number;
  }> = formValues(state, "optionalVenues") ?? [];

  const optionalVenues = formOptionalVenues
    .filter((venue) => !!venue.selectedPlaceId)
    .map((venue) => ({
      placeId: venue.selectedPlaceId || null,
      companyName: venue.selectedPlaceAddressComponents.companyName || null,
      address1: venue.selectedPlaceAddressComponents.address1,
      city: venue.selectedPlaceAddressComponents.city,
      state: venue.selectedPlaceAddressComponents.state,
      country: venue.selectedPlaceAddressComponents.country,
      zip: venue.selectedPlaceAddressComponents.zip,
      utcOffset: venue.selectedPlaceUtcOffset || 0,
      municipalTaxCode: null,
      facilityCode: null,
      venueCode: null,
    }));

  const setUpDates: string[] = (formValues(state, "eventSetupDates") ?? [])
    .filter(({ selected }: { selected: boolean }) => selected)
    .map(({ key: date }: { key: string }) =>
      dateHelpers.dateOnlyStringFormat(date),
    );

  const tearDownDates: string[] = (
    formValues(state, "eventTeardownDates") ?? []
  )
    .filter(({ selected }: { selected: boolean }) => selected)
    .map(({ key: date }: { key: string }) =>
      dateHelpers.dateOnlyStringFormat(date),
    );

  const includeRehearsalDinner = isWeddingTypeEventSelected(state)
    ? !!(formValues(state, "weddingRehearsal") as unknown)
    : null;

  const includeBreakfastAfterWedding = isWeddingTypeEventSelected(state)
    ? !!(formValues(state, "weddingBrunch") as unknown)
    : null;

  const northDakotaDisclosure = isVenueInNorthDakota(state)
    ? !!(formValues(state, "northDakotaDisclosure") as unknown)
    : null;

  const venue = state.formPage.venue;

  const amountDetails = {
    totalAmount: getTotalAmount(state),
    currency: "USD",
  };

  return {
    venueSearchType: formValues(state, "venueSearchTypeRadio"),
    isKentuckyEntity: formValues(state, "kentuckyStateEntity") === "yes",
    isFederalEntity: formValues(state, "federalEntity") === "yes",
    eventType: state.form.application.values.eventType?.value?.identifier ?? "",
    includeRehearsalDinner,
    includeBreakfastAfterWedding,
    northDakotaDisclosure,
    gll: formValues(state, "generalLiabilityCoverage"),
    name: formValues(state, "eventName"),
    dates: (formValues(state, "eventDates") as string[]).map((date) =>
      dateHelpers.dateOnlyStringFormat(date),
    ),
    setUpDates,
    tearDownDates,
    eventFrequency: formValues(state, "eventFrequencyField"),
    averageDailyAttendance: Number(formValues(state, "eventDailyGuests")),
    additionalCoverage: getAdditionalCoverages(state),
    renterAddress: [renterAddress],
    venueAddress: [
      {
        placeId: venue.selectedPlaceId || null,
        facilityCode: formValues(state, "facilityCode") || null,
        venueCode: formValues(state, "venueCode") || null,
        companyName:
          venue.selectedPlaceAddressComponents.companyName ||
          formValues(state, "manualVenueName"),
        address1:
          venue.selectedPlaceAddressComponents.address1 ||
          formValues(state, "manualVenueAddress1"),
        city:
          venue.selectedPlaceAddressComponents.city ||
          formValues(state, "manualVenueCity"),
        state:
          venue.selectedPlaceAddressComponents.state ||
          formValues(state, "manualVenueState"),
        country:
          venue.selectedPlaceAddressComponents.country ||
          formValues(state, "manualVenueCountry"),
        zip:
          venue.selectedPlaceAddressComponents.zip ||
          formValues(state, "manualVenueZip"),
        utcOffset: venue.selectedPlaceUtcOffset || 0,
        municipalTaxCode: formValues(state, "venueMunicipalityCode") || null,
      },
      ...optionalVenues,
    ],
    insuranceContactPhone: formValues(state, "contactPhone"),
    insuranceContactEmail: formValues(state, "contactEmail"),
    insuranceContactAddress: [insuranceContactAddress],
    consentConfirmed: formValues(state, "consentConfirmed"),
    excludedActivitiesConfirmed: true,
    sourceCode: state.formPage.referral.sourceCode,
    producerCode: state.formPage.referral.producerCode,
    subProducerCode: state.formPage.referral.subProducerCode,
    facilityCode: state.formPage.referral.facilityCode,
    underwritingQuestions,
    boilerplate: state.formPage.referral.boilerplate,
    additionalInsureds: state.formPage.summary.additionalCertHolders,
    amountDetails,
  };
}

export default getPaymentBody;
